import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MonitorPlay, Smartphone } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { LanguageToggle } from '@/components/LanguageToggle';
import { HowItWorks } from '@/components/landing/HowItWorks';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import { FREE_SIGNUP_CREDITS } from '@/lib/config';

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const ctaTo = user ? '/studyo' : '/kayit';

  const cards = [
    {
      icon: MonitorPlay,
      title: t('landing.card1Title'),
      ratio: '16:9',
      out: '1920 x 1080',
      desc: t('landing.card1Desc'),
    },
    {
      icon: Smartphone,
      title: t('landing.card2Title'),
      ratio: '9:16',
      out: '1080 x 1920',
      desc: t('landing.card2Desc'),
    },
  ];

  return (
    <div className="min-h-screen noise-bg text-[hsl(var(--text))]">
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-2))]">
        <div className="mx-auto flex h-14 max-w-[1200px] items-center px-4 sm:px-6 lg:px-8">
          <span className="shrink-0 sm:hidden">
            <Logo compact />
          </span>
          <span className="hidden shrink-0 sm:inline-flex">
            <Logo />
          </span>
          <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2">
            {user ? (
              <>
                <Button asChild data-testid="landing-studio-button" className="h-9 shrink-0 whitespace-nowrap bg-[hsl(var(--brand-red))] px-3 text-[13px] text-white hover:bg-[hsl(var(--brand-red-2))] sm:px-4 sm:text-sm">
                  <Link to="/studyo">{t('landing.goStudio')}</Link>
                </Button>
                <LanguageToggle />
              </>
            ) : (
              <>
                <Button asChild variant="ghost" data-testid="landing-login-button" className="h-9 shrink-0 whitespace-nowrap px-2.5 text-[13px] text-[hsl(var(--text-2))] hover:bg-[hsl(var(--surface))] hover:text-white sm:px-4 sm:text-sm">
                  <Link to="/giris">{t('landing.login')}</Link>
                </Button>
                <Button asChild data-testid="landing-register-button" className="h-9 shrink-0 whitespace-nowrap bg-[hsl(var(--brand-red))] px-3 text-[13px] text-white hover:bg-[hsl(var(--brand-red-2))] sm:px-4 sm:text-sm">
                  <Link to="/kayit">{t('landing.tryNow')}</Link>
                </Button>
                <LanguageToggle />
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 opacity-[0.14]"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(14,14,14,0.86) 0%, rgba(11,11,11,0.95) 60%, rgb(15,15,15) 100%)',
            }}
          />
          <div className="absolute inset-x-0 top-0 h-[460px] grid-lines" />
        </div>

        <div className="relative mx-auto max-w-[1100px] px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <h1 data-testid="hero-title" className="font-display text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
              {t('landing.heroLine1')}
              <br />
              <span className="text-[hsl(var(--brand-red))]">{t('landing.heroLine2')}</span>
            </h1>
            <p data-testid="hero-description" className="mt-7 max-w-2xl text-base leading-relaxed text-[hsl(var(--text-2))] sm:text-lg">
              {t('landing.heroDesc')}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-cta-button"
                className="h-13 bg-[hsl(var(--brand-red))] px-7 py-3.5 text-base font-semibold text-white transition-colors duration-150 hover:bg-[hsl(var(--brand-red-2))] active:scale-[0.98]"
              >
                <Link to={ctaTo}>
                  {t('landing.tryNow')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <span data-testid="hero-free-badge" className="rounded-full border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] px-3.5 py-2 text-sm font-medium text-[hsl(var(--text-2))]">
                {t('landing.freeBadge', { count: FREE_SIGNUP_CREDITS })}
              </span>
            </div>
          </motion.div>

          <div className="mt-20 grid gap-5 sm:grid-cols-2">
            {cards.map((c, i) => (
              <motion.div
                key={c.ratio}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.08 }}
                className="panel flex items-center gap-5 p-6"
              >
                <div className="grid h-24 w-24 shrink-0 place-items-center rounded-[16px] bg-[hsl(var(--surface-2))]">
                  <c.icon
                    className="text-[hsl(var(--brand-red))]"
                    strokeWidth={1.4}
                    style={{ width: 56, height: 56 }}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-xl font-semibold">{c.title}</h3>
                    <span className="rounded-full bg-[hsl(var(--brand-red))] px-2 py-0.5 text-[11px] font-bold text-white">
                      {c.ratio}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[hsl(var(--text-muted))]">{c.desc}</p>
                  <p className="mt-2 font-mono-alt text-xs text-[hsl(var(--text-muted))]">{c.out} px</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks />

      <footer className="border-t border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-2))]">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center justify-between gap-3 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
          <Logo />
          <p className="text-xs text-[hsl(var(--text-muted))]">{t('landing.footer')}</p>
        </div>
      </footer>
    </div>
  );
}
